import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MasterCrudPage } from '@/components/MasterCrudPage.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { FormField, Input } from '@/components/ui/Input.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { api } from '@/lib/api.js';
import { RefreshCw } from 'lucide-react';

function InitBalancesButton() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  const init = useMutation({
    mutationFn: () => api.post('/leave/init-balances', { year: Number(year) }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['leaveBalances'] });
      setOpen(false);
      toast({ variant: 'success', title: 'Balances initialised', description: `Created ${data.created}, skipped ${data.skipped} existing records.` });
    },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}><RefreshCw className="h-4 w-4" />Init Balances</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Initialise Leave Balances</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">
            For every active employee and every active leave type, an opening balance row will be created using the <strong>Annual Entitlement Days</strong> you configured. Existing rows are kept unchanged.
          </p>
          <FormField label="Year">
            <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
          </FormField>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => init.mutate()} disabled={init.isPending}>
              {init.isPending ? 'Running…' : 'Initialise'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function LeaveTypesPage() {
  return (
    <MasterCrudPage
      title="Leave Types"
      description="Configure leave types and yearly entitlements"
      endpoint="/leave-types"
      queryKey={['leaveTypes']}
      extraActions={<InitBalancesButton />}
      fields={[
        { name: 'code', label: 'Code', required: true, placeholder: 'e.g. AL' },
        { name: 'name', label: 'Name', required: true },
        { name: 'annual_entitlement_days', label: 'Annual Entitlement Days', type: 'number', step: '0.5', default: 0 },
        { name: 'paid', label: 'Paid Leave', type: 'select', default: 1, options: [
          { value: 1, label: 'Yes' }, { value: 0, label: 'No' },
        ]},
        { name: 'carry_forward', label: 'Carry Forward', type: 'select', default: 0, options: [
          { value: 0, label: 'No' }, { value: 1, label: 'Yes' },
        ]},
      ]}
      serialize={(v) => ({
        ...v,
        paid: Number(v.paid),
        carry_forward: Number(v.carry_forward),
        annual_entitlement_days: Number(v.annual_entitlement_days),
      })}
      columns={[
        { key: 'code', header: 'Code', sortable: true, className: 'font-mono' },
        { key: 'name', header: 'Name', sortable: true },
        { key: 'annual_entitlement_days', header: 'Entitlement Days', className: 'text-right tabular-nums' },
        { key: 'paid', header: 'Paid', render: (r) => (r.paid ? 'Yes' : 'No') },
        { key: 'carry_forward', header: 'Carry Fwd', render: (r) => (r.carry_forward ? 'Yes' : 'No') },
      ]}
    />
  );
}
